import type { ServicePageData } from './types';

const popUpDisplays: ServicePageData = {
  slug: 'pop-up-displays',
  eyebrow: 'Display',
  eyebrowTo: '/services/all-display',
  title: 'Set up your stand in',
  titleAccent: 'minutes, not hours.',
  intro:
    'Pop-up stands and fabric backdrops give you a full-height branded presence for exhibitions and events, and pack down into a carry case afterwards. No tools, no long build time.',
  heroImages: [
    '/home/display/oc-flag.jpg',
    '/home/gazebos/barkavenue-gazebo.jpg',
    '/home/gazebos/curry-king-gazebo.jpg',
    '/home/gazebos/wildjac-gazebo.jpg',
    '/home/gazebos/mayfield-gazebo.jpg',
    '/home/parasols/parasol-mockup.jpg',
    '/home/parasols/petal-bar-parasol.jpg',
    '/home/tablecloths/tablecloths-conference-studio.jpg',
    '/home/tablecloths/tablecloths-box-fitted-studio.jpg',
    '/home/services/gazebos.jpg',
    '/home/services/parasols.jpg',
    '/home/display/oc-flag.jpg',
    '/home/gazebos/nightstop-gazebo.jpg',
  ],

  useCases: [
    { title: 'Exhibitions and trade shows', text: 'A full-height branded backdrop that goes up in minutes.' },
    { title: 'Conferences and seminars', text: 'A professional backdrop for talks, registration desks and photo points.' },
    { title: 'Retail and pop-up events', text: 'Portable branding for temporary stalls and product launches.' },
    { title: 'Reception and office spaces', text: 'A quick way to add branding to a meeting room or entrance without redecorating.' },
    { title: 'Roadshows', text: 'Lightweight kit that travels well between multiple venues.' },
  ],

  optionsHeading: 'Choose your stand',
  optionsIntro: 'From a simple straight backdrop to a full tension-fabric frame, all supplied with a carry case.',
  options: [
    {
      id: 'straight-pop-up',
      title: 'Straight pop-up',
      lead: 'The classic flat pop-up backdrop, quick to assemble and easy to store.',
      body: 'A concertina aluminium frame that unfolds and locks into shape, with a printed graphic panel that Velcros onto the front. The most affordable full-height option.',
      image: '/home/gazebos/barkavenue-gazebo.jpg',
      specs: [
        { label: 'Size', value: '2 x 2.25m standard, wider configurations available.' },
        { label: 'Frame', value: 'Concertina aluminium, magnetic bars.' },
        { label: 'Graphic', value: 'Full-colour printed PVC or fabric panel.' },
      ],
    },
    {
      id: 'curved-pop-up',
      title: 'Curved pop-up',
      lead: 'The same mechanism with a curved profile for a softer, more premium look.',
      body: 'Popular on exhibition stands where a straight edge feels flat against neighbouring displays. Assembles the same way as a straight pop-up.',
      image: '/home/gazebos/curry-king-gazebo.jpg',
      specs: [
        { label: 'Size', value: '2 x 2.25m standard, wider configurations available.' },
        { label: 'Frame', value: 'Concertina aluminium, curved profile.' },
        { label: 'Graphic', value: 'Full-colour printed PVC or fabric panel.' },
      ],
    },
    {
      id: 'tension-fabric-frames',
      title: 'Tension-fabric frames',
      lead: 'A lightweight aluminium frame with a stretch-fit fabric skin.',
      body: 'The fabric skin zips around the frame and pulls drum-tight for a smooth, wrinkle-free finish. Lighter to carry than a panel-based pop-up and easier to reprint when your message changes.',
      image: '/home/gazebos/wildjac-gazebo.jpg',
      specs: [
        { label: 'Size', value: 'Standard and custom sizes, straight or curved.' },
        { label: 'Frame', value: 'Lightweight aluminium tube.' },
        { label: 'Graphic', value: 'Dye-sublimation printed stretch fabric, machine washable.' },
      ],
    },
    {
      id: 'counter-podium',
      title: 'Counter and podium units',
      lead: 'A branded counter to sit alongside your main backdrop.',
      body: 'Useful as a reception point, product shelf or somewhere to rest literature and a card machine. Pop-up construction to match your backdrop.',
      image: '/home/tablecloths/tablecloths-conference-studio.jpg',
      specs: [
        { label: 'Size', value: 'Standard counter height, various widths.' },
        { label: 'Frame', value: 'Pop-up aluminium frame with a lockable storage cupboard.' },
        { label: 'Graphic', value: 'Full-colour printed wrap-around panel.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Full-colour print',
      text: 'Your artwork printed directly onto PVC, fabric or a dye-sublimation panel for a seamless, edge-to-edge finish.',
    },
    {
      title: 'Cut vinyl accents',
      text: 'Logos or lettering added as a cut vinyl layer over a plain background, useful for a simpler, budget-friendly graphic.',
    },
  ],

  gallery: [
    '/home/display/oc-flag.jpg',
    '/home/gazebos/barkavenue-gazebo.jpg',
    '/home/gazebos/curry-king-gazebo.jpg',
    '/home/gazebos/wildjac-gazebo.jpg',
    '/home/gazebos/mayfield-gazebo.jpg',
    '/home/tablecloths/tablecloths-box-fitted-studio.jpg',
    '/home/parasols/parasol-mockup.jpg',
  ],

  info: [
    {
      title: 'Supply only',
      text: 'Every stand comes with a carry case and clear assembly instructions, no tools needed.',
      bullets: ['Collect from Kidderminster', 'Delivery from £10'],
    },
    {
      title: 'Care',
      text: 'Wipe PVC panels clean with a soft cloth. Fabric skins can be gently machine washed and re-tensioned onto the frame.',
    },
    {
      title: 'Turnaround',
      text: 'Production usually takes 5 to 10 working days from approval. Got a deadline? Talk to us and we will do our best.',
    },
  ],

  faqs: [
    {
      q: 'How long does it take to set up on site?',
      a: 'A straight or curved pop-up typically takes one person a few minutes to unfold and lock into place. Tension-fabric frames take slightly longer to zip the skin on.',
    },
    {
      q: 'Can I reuse the frame and just replace the graphic later?',
      a: 'Yes. Frames are built to last, so when your branding changes you usually only need a new panel or fabric skin, not a whole new stand.',
    },
  ],

  cta: {
    title: 'Get your free pop-up display quote.',
    text: 'Tell us about your event and we will recommend the right stand and graphic finish.',
    image: '/home/services/gazebos.jpg',
  },
};

export default popUpDisplays;
